// API-Football wrapper for Pickfox.
//
// Reads three env vars from pickfox-app/.env (and Vercel for prod):
//   VITE_API_FOOTBALL_KEY              — the secret key from api-football.com
//   VITE_API_FOOTBALL_TIER             — "free" or "pro" (controls UI gating)
//   VITE_API_FOOTBALL_DEFAULT_SEASON   — e.g. "2024" on free, "2026" on pro
//
// Free tier is limited to 100 requests/day and seasons 2022–2024.
// Pro tier (€19/mo) unlocks current season + 7500 req/day.

const API_KEY = import.meta.env.VITE_API_FOOTBALL_KEY;
const BASE = "https://v3.football.api-sports.io";

export const API_TIER = import.meta.env.VITE_API_FOOTBALL_TIER || "free";
export const isProTier = () => API_TIER === "pro";

export const DEFAULT_SEASON = parseInt(
  import.meta.env.VITE_API_FOOTBALL_DEFAULT_SEASON || "2024",
  10
);

// API-Football's internal league IDs. Lookup at:
// https://dashboard.api-football.com/soccer/ids/leagues
export const LEAGUE_IDS = {
  PL: 39,   // Premier League
  LL: 140,  // La Liga
  SA: 135,  // Serie A
  BL: 78,   // Bundesliga
  SL: 119,  // Superliga
  CL: 2,    // Champions League
  WC: 1,    // FIFA World Cup (VM 2026 = season 2026 — locked to Pro tier on free)
};

// ---------- low-level fetch ----------

async function apiFetch(path, params = {}) {
  if (!API_KEY) {
    throw new Error(
      "Missing VITE_API_FOOTBALL_KEY env var. Add it to pickfox-app/.env."
    );
  }
  const url = new URL(BASE + path);
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") {
      url.searchParams.set(k, String(v));
    }
  }
  const res = await fetch(url.toString(), {
    headers: { "x-apisports-key": API_KEY },
  });
  if (!res.ok) {
    throw new Error(`API-Football HTTP ${res.status}: ${res.statusText}`);
  }
  const json = await res.json();
  if (json.errors && Object.keys(json.errors).length > 0) {
    const msg =
      typeof json.errors === "object"
        ? JSON.stringify(json.errors)
        : String(json.errors);
    throw new Error("API-Football error: " + msg);
  }
  return json;
}

// ---------- public endpoints ----------

// Cheap; returns account info + today's request counter.
// Use this for the "Test API-Football" button in Operator.
export async function getStatus() {
  const json = await apiFetch("/status");
  return json.response;
}

// Fixtures for a league + season, optionally narrowed to a date range (YYYY-MM-DD).
// Costs 1 request regardless of how many fixtures come back.
export async function getFixtures({
  league,
  season = DEFAULT_SEASON,
  from,
  to,
} = {}) {
  const json = await apiFetch("/fixtures", { league, season, from, to });
  return json.response || [];
}

// Single fixture by API-Football fixture id. Useful for refreshing one match.
export async function getFixtureById(fixtureId) {
  const json = await apiFetch("/fixtures", { id: fixtureId });
  return (json.response && json.response[0]) || null;
}

// Odds for one fixture from one bookmaker.
// bookmaker=8 is Bet365 by default — change if Mikkel prefers another.
export async function getOddsForFixture(fixtureId, bookmakerId = 8) {
  const json = await apiFetch("/odds", {
    fixture: fixtureId,
    bookmaker: bookmakerId,
  });
  return (json.response && json.response[0]) || null;
}

// Player stats for one fixture. Used to surface Layer 3 player props
// (e.g. "Saka scores", "Cornelius hat-trick"). Costs 1 request.
export async function getPlayersForFixture(fixtureId) {
  const json = await apiFetch("/players", { fixture: fixtureId });
  return json.response || [];
}

// ---------- mapping helpers ----------

// Convert an API-Football fixture into a partial row for our `matches` table.
// Layers are left empty here — they get populated by a separate odds-fetch step
// once we've decided how to map bet types into our 3-layer structure.
//
// `api_fixture_id` is the API-Football fixture id stored on the matches row so we
// can later refresh stats/scores for that match. Added in migration 011.
export function fixtureToMatchSkeleton(fixture, leagueCode) {
  const f = fixture.fixture;
  const teams = fixture.teams;
  return {
    league_id: leagueCode,
    home_team: teams.home.name,
    away_team: teams.away.name,
    home_short: shortenTeam(teams.home.name),
    away_short: shortenTeam(teams.away.name),
    kickoff_at: f.date,
    is_bonus: false,
    layer1_options: [],
    layer2_options: [],
    layer3_options: [],
    api_fixture_id: f.id,
  };
}

function shortenTeam(name) {
  const cleaned = name.replace(/\s+(FC|FF|IF|CF|AS|SC|SE)\.?$/i, "").trim();
  const parts = cleaned.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 3).toUpperCase();
  return parts
    .map((p) => p[0])
    .join("")
    .slice(0, 3)
    .toUpperCase();
}
